import { getCookie, setCookie } from '../../../DAL/storage-provider';
import { getJSONFromHtml } from '../../../domain/common';
import { getHiddenDataBoolean, getHiddenDataString } from '../../../domain/hidden-data-provider';
import { format } from 'date-fns';

export const GDRP_COOKIE_NAME = 'gdpr2';
export const GPCSB_SESSION_STORAGE_KEY = 'GPCSB';
export const GPC_COOKIE = 'ORGPC';

export interface IGdpr {
    IsCountryGDPR: string, // "y", "n", "u"
    IsMarketing: boolean,
    IsPerformance: boolean,
    IsFunctional: boolean,
    ItemModifiedWhen: string, // "2022-07-31T12:57:15.7440792"
}

export const getGdprCountry = () => {
    return getHiddenDataString('gdpr-country') || 'u';
};

/**
 * In case GDPR cookie doesn't exist, we rely on "IsGDPRDefaultOptOut" returned from SP.
 * If user is opt out ---> both IsMarketing and IsPerformance are false.
 * If user is not opt out ---> both IsMarketing and IsPerformance are true.
 * https://g6a420.axshare.com/#g=1&p=scripts_per_cookie_flow
 */
export const getGdprDefaultValues = () : IGdpr => {
    const isOptOut = isGdprOptOut();

    return {
        IsMarketing: !isOptOut,
        IsPerformance: !isOptOut,
        IsFunctional: !isOptOut,
        ItemModifiedWhen: format(new Date(), 'MM/dd/yyyy HH:mm:ss'),
        IsCountryGDPR: getGdprCountry(),
    } as IGdpr;
};

/**
 * https://wavesaudio.atlassian.net/browse/DSWEB-4442
 * https://g6a420.axshare.com/#g=1&p=scripts_per_cookie_flow
 */
export const getGdprCookie = () : IGdpr => {
    const cookie = getCookie(GDRP_COOKIE_NAME);
    if(!cookie) return getGdprDefaultValues();

    let parsed: IGdpr | null = null;

    try{
        parsed = JSON.parse(cookie) as IGdpr;
    }
    catch(ex){}

    return parsed || getGdprDefaultValues();
};


export const setGDPRCookie = (data: IGdpr) => {

    // expiration is in minutes
    const oneYearInMinutes = 60 * 24 * 365;

    /*
    Old settings:
    ------------------
    const expiration = -1;
    const secure = false;
    const sameSite = undefined;
    const preventStringify = false;
    const domain = '.waves.com';*/

    const secure = true;
    const sameSite = 'Lax';
    const preventStringify = false;
    const domain = '.waves.com';

    setCookie('gdpr2', data, oneYearInMinutes, secure, sameSite, preventStringify, domain);
};

export const getGdprCookieFromServer = () : IGdpr => {
    return getJSONFromHtml(document.getElementById('cookies-policy-data-json')) as IGdpr;
};

export const isGdprOptOut = () : boolean => {
    return getHiddenDataBoolean('gdpr-opt-out');
};

/**
 * <div class="msg-box text-left mb-12">You may find this content helpful: To be able to view it, <a href="/legal/cookie-notice-and-privacy-choices" target="_top" title="">allow Performance Cookies here</a>.</div>
 */
export const getGdprError = () => {
    const $error = document.getElementById('gdpr-error');
    return ($error?.textContent || '').trim();
};

/**
 * https://wavesaudio.atlassian.net/browse/DSWEB-5151
 */
export const isGpcEnabled = (): boolean => {
    // @ts-ignore
    return !!navigator.globalPrivacyControl;
};

export const setGPCBannerAlreadyShown = () => {
    sessionStorage.setItem(GPCSB_SESSION_STORAGE_KEY, '1');
};

export const checkIfGPCBannerAlreadyShown = (): boolean => {
    return sessionStorage.getItem(GPCSB_SESSION_STORAGE_KEY) === '1';
};

export function setGpcSessionCookie(): void {
    try {
        document.cookie =
            `${encodeURIComponent(GPC_COOKIE)}=1` +
            `; Path=/` +
            `; Secure` +
            `; SameSite=Lax`;

        // Session cookie:
        // Do NOT set Expires or Max-Age.

        // Same domain / current host:
        // Do NOT set Domain.

        // Domain=.waves.com // cannot always be reliably set from localhost/subdomains
    }
    catch (error) {
        console.error('Error: SetGpcSessionCookie', error);
    }
}